import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "react-hot-toast"
import {
  FiX, FiAlertCircle, FiUploadCloud, FiLink, FiClock,
  FiPlusCircle, FiTrash2, FiCode, FiCheckCircle, FiFileText,
} from "react-icons/fi"
import { useDispatch, useSelector } from "react-redux"

import {
  createSubSection,
  updateSubSection,
} from "../../../../../services/operations/courseDetailsAPI"
import { setCourse } from "../../../../../slices/courseSlice"
import RichTextEditor from "../../../../common/RichTextEditor"
import Upload from "../Upload"

// ── helpers ───────────────────────────────────────────────────────────────────

const looksLikeVideoUrl = (s) => {
  try {
    const u = new URL(s)
    return u.protocol === "http:" || u.protocol === "https:"
  } catch { return false }
}

const emptyMCQ = () => ({ question: "", options: ["", "", "", ""], correctIndex: 0 })

const defaultQuiz = () => ({
  mcqQuestions:    [emptyMCQ()],
  codingChallenge: {
    enabled:     false,
    language:    "javascript",
    problem:     "",
    starterCode: "",
    testCases:   [{ input: "", expectedOutput: "", label: "" }],
  },
  passingScore: 70,
})

// ── sub-components ────────────────────────────────────────────────────────────

function MCQBuilder({ questions, onChange, disabled }) {
  const update = (idx, patch) => {
    const updated = questions.map((q, i) => i === idx ? { ...q, ...patch } : q)
    onChange(updated)
  }
  const updateOption = (qIdx, oIdx, val) => {
    const opts = [...questions[qIdx].options]
    opts[oIdx] = val
    update(qIdx, { options: opts })
  }

  return (
    <div className="flex flex-col gap-4">
      {questions.map((q, qi) => (
        <div key={qi} className="rounded-xl border border-richblack-600 bg-richblack-700/50 p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-richblack-300">Question {qi + 1}</span>
            {questions.length > 1 && !disabled && (
              <button type="button" onClick={() => onChange(questions.filter((_, i) => i !== qi))}
                className="text-pink-300 hover:text-pink-200">
                <FiTrash2 size={14} />
              </button>
            )}
          </div>

          <input
            disabled={disabled}
            value={q.question}
            onChange={(e) => update(qi, { question: e.target.value })}
            placeholder="Enter question text"
            className="form-style mb-3 w-full text-sm"
          />

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {q.options.map((opt, oi) => (
              <label key={oi}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                  q.correctIndex === oi
                    ? "border-caribbeangreen-300/60 bg-caribbeangreen-300/10"
                    : "border-richblack-600 bg-richblack-800"
                }`}
              >
                <input
                  type="radio"
                  disabled={disabled}
                  checked={q.correctIndex === oi}
                  onChange={() => update(qi, { correctIndex: oi })}
                  className="accent-caribbeangreen-300"
                />
                <input
                  disabled={disabled}
                  value={opt}
                  onChange={(e) => updateOption(qi, oi, e.target.value)}
                  placeholder={`Option ${oi + 1}`}
                  className="w-full bg-transparent text-sm text-richblack-5 outline-none placeholder:text-richblack-500"
                />
                {q.correctIndex === oi && (
                  <FiCheckCircle size={14} className="shrink-0 text-caribbeangreen-300" />
                )}
              </label>
            ))}
          </div>
        </div>
      ))}

      {!disabled && (
        <button type="button"
          onClick={() => onChange([...questions, emptyMCQ()])}
          className="flex items-center gap-2 self-start rounded-lg border border-dashed border-richblack-500 px-4 py-2 text-xs text-richblack-300 hover:border-yellow-50/40 hover:text-yellow-50">
          <FiPlusCircle size={14} /> Add Question
        </button>
      )}
    </div>
  )
}

function CodingChallengeBuilder({ data, onChange, disabled }) {
  const set = (patch) => onChange({ ...data, ...patch })
  const setTestCase = (idx, patch) => {
    const updated = data.testCases.map((tc, i) => i === idx ? { ...tc, ...patch } : tc)
    set({ testCases: updated })
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Toggle */}
      <label className="flex cursor-pointer items-center gap-3">
        <div
          onClick={() => !disabled && set({ enabled: !data.enabled })}
          className={`relative h-5 w-9 rounded-full transition-colors ${data.enabled ? "bg-caribbeangreen-300" : "bg-richblack-600"}`}
        >
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${data.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
        </div>
        <span className="text-sm font-medium text-richblack-5">Include Coding Challenge</span>
      </label>

      {data.enabled && (
        <>
          {/* Language */}
          <div className="flex gap-2">
            {["javascript", "python"].map((lang) => (
              <button key={lang} type="button" disabled={disabled}
                onClick={() => set({ language: lang })}
                className={`rounded-lg border px-4 py-1.5 text-xs font-medium transition-colors capitalize ${
                  data.language === lang
                    ? "border-yellow-50/50 bg-yellow-50/10 text-yellow-50"
                    : "border-richblack-600 text-richblack-400 hover:text-richblack-200"
                }`}
              >
                {lang}
              </button>
            ))}
          </div>

          {/* Problem */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-richblack-400">Problem Statement</label>
            <textarea
              disabled={disabled}
              value={data.problem}
              onChange={(e) => set({ problem: e.target.value })}
              placeholder="Describe the coding problem. Students will read this before writing code."
              rows={4}
              className="form-style w-full resize-none text-sm"
            />
          </div>

          {/* Starter code */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-richblack-400">Starter Code <span className="normal-case font-normal">(optional)</span></label>
            <textarea
              disabled={disabled}
              value={data.starterCode}
              onChange={(e) => set({ starterCode: e.target.value })}
              placeholder={data.language === "python" ? "def solution(n):\n    # your code here\n    pass" : "function solution(n) {\n  // your code here\n}"}
              rows={5}
              className="form-style w-full resize-none font-mono text-xs"
            />
          </div>

          {/* Test cases */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-richblack-400">Test Cases</label>
            <p className="text-xs text-richblack-500">
              Student code must print the expected output to stdout. Use <code className="text-yellow-50">console.log</code> (JS) or <code className="text-yellow-50">print()</code> (Python).
            </p>
            {data.testCases.map((tc, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 rounded-lg border border-richblack-600 bg-richblack-700/50 p-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-richblack-400">Input</span>
                  <textarea
                    disabled={disabled}
                    value={tc.input}
                    onChange={(e) => setTestCase(i, { input: e.target.value })}
                    placeholder="stdin input (or empty)"
                    rows={2}
                    className="form-style w-full resize-none font-mono text-xs"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-richblack-400">Expected Output</span>
                  <textarea
                    disabled={disabled}
                    value={tc.expectedOutput}
                    onChange={(e) => setTestCase(i, { expectedOutput: e.target.value })}
                    placeholder="expected stdout"
                    rows={2}
                    className="form-style w-full resize-none font-mono text-xs"
                  />
                </div>
                <div className="flex flex-col items-center justify-center gap-1 pt-4">
                  {data.testCases.length > 1 && !disabled && (
                    <button type="button"
                      onClick={() => set({ testCases: data.testCases.filter((_, j) => j !== i) })}
                      className="text-pink-300 hover:text-pink-200">
                      <FiTrash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!disabled && (
              <button type="button"
                onClick={() => set({ testCases: [...data.testCases, { input: "", expectedOutput: "", label: "" }] })}
                className="flex items-center gap-2 self-start rounded-lg border border-dashed border-richblack-500 px-4 py-2 text-xs text-richblack-300 hover:border-yellow-50/40 hover:text-yellow-50">
                <FiPlusCircle size={14} /> Add Test Case
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ── PDF links builder ─────────────────────────────────────────────────────────

function PDFLinksBuilder({ links, onChange, disabled }) {
  const update = (idx, patch) =>
    onChange(links.map((l, i) => (i === idx ? { ...l, ...patch } : l)))

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-richblack-5">
          PDF / Resource Links <span className="text-xs font-normal text-richblack-400">(optional)</span>
        </label>
      </div>

      {links.length > 0 && (
        <div className="flex flex-col gap-2">
          {links.map((link, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
              <input
                disabled={disabled}
                value={link.name}
                onChange={(e) => update(i, { name: e.target.value })}
                placeholder="Display name (e.g. Lecture Notes)"
                className="form-style w-full text-sm"
              />
              <div className="relative">
                <FiFileText size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-richblack-400" />
                <input
                  type="url"
                  disabled={disabled}
                  value={link.url}
                  onChange={(e) => update(i, { url: e.target.value })}
                  placeholder="https://…/file.pdf"
                  className="form-style w-full !pl-8 text-sm"
                />
              </div>
              {!disabled && (
                <button type="button"
                  onClick={() => onChange(links.filter((_, j) => j !== i))}
                  className="rounded-lg p-2 text-richblack-500 hover:text-pink-300">
                  <FiTrash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!disabled && (
        <button type="button"
          onClick={() => onChange([...links, { name: "", url: "" }])}
          className="flex items-center gap-2 self-start rounded-lg border border-dashed border-richblack-500 px-4 py-2 text-xs text-richblack-300 hover:border-yellow-50/40 hover:text-yellow-50">
          <FiPlusCircle size={14} /> Add PDF Link
        </button>
      )}
    </div>
  )
}

// ── main modal ────────────────────────────────────────────────────────────────

export default function SubSectionModal({
  modalData,
  setModalData,
  add = false,
  view = false,
  edit = false,
}) {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
  } = useForm({ shouldUnregister: true })

  const dispatch = useDispatch()
  const { token } = useSelector((state) => state.auth)
  const { course } = useSelector((state) => state.course)

  const [loading, setLoading] = useState(false)
  const [description, setDescription] = useState("")
  const [descError, setDescError] = useState(false)

  // video fields
  const [videoMode, setVideoMode] = useState("upload")
  const [videoUrl, setVideoUrl] = useState("")
  const [videoUrlError, setVideoUrlError] = useState(false)
  const [videoFileError, setVideoFileError] = useState(false)
  const [timeDuration, setTimeDuration] = useState("")

  // type + quiz
  const [subType, setSubType] = useState("video")
  const [quizState, setQuizState] = useState(defaultQuiz())
  const [pdfLinks, setPdfLinks] = useState([])

  useEffect(() => {
    if (view || edit) {
      setValue("lectureTitle", modalData.title)
      setDescription(modalData.description || "")
      setSubType(modalData.type || "video")

      if (modalData.videoUrl) {
        setVideoUrl(modalData.videoUrl)
        setVideoMode("url")
      }
      if (modalData.timeDuration) setTimeDuration(modalData.timeDuration)
      if (modalData.pdfLinks?.length) setPdfLinks(modalData.pdfLinks)

      if (modalData.type === "quiz" && modalData.quizData) {
        const qd = modalData.quizData
        setQuizState({
          mcqQuestions: qd.mcqQuestions?.length ? qd.mcqQuestions.map((q) => ({
            question:     q.question,
            options:      q.options,
            correctIndex: q.correctIndex,
          })) : [emptyMCQ()],
          codingChallenge: {
            enabled:     !!(qd.codingChallenge?.problem),
            language:    qd.codingChallenge?.language || "javascript",
            problem:     qd.codingChallenge?.problem || "",
            starterCode: qd.codingChallenge?.starterCode || "",
            testCases:   qd.codingChallenge?.testCases?.length
              ? qd.codingChallenge.testCases
              : [{ input: "", expectedOutput: "", label: "" }],
          },
          passingScore: qd.passingScore ?? 70,
        })
      }
    }
  }, [])

  const handleModeSwitch = (mode) => {
    setVideoMode(mode)
    setVideoUrlError(false)
    setVideoFileError(false)
  }

  const isFormUpdated = () => {
    const v = getValues()
    return (
      v.lectureTitle   !== modalData.title ||
      description      !== modalData.description ||
      subType          !== (modalData.type || "video") ||
      timeDuration     !== (modalData.timeDuration || "") ||
      JSON.stringify(pdfLinks) !== JSON.stringify(modalData.pdfLinks || []) ||
      (videoMode === "url"
        ? videoUrl !== modalData.videoUrl
        : v.lectureVideo instanceof File)
    )
  }

  const validateVideo = () => {
    // Video is optional — only validate URL format if one was actually entered
    if (subType !== "video") return true
    if (videoMode === "url" && videoUrl.trim() && !looksLikeVideoUrl(videoUrl)) {
      setVideoUrlError(true)
      return false
    }
    return true
  }

  const buildQuizPayload = () => {
    const { mcqQuestions, codingChallenge, passingScore } = quizState
    const payload = { mcqQuestions, passingScore }
    if (codingChallenge.enabled) {
      payload.codingChallenge = {
        language:    codingChallenge.language,
        problem:     codingChallenge.problem,
        starterCode: codingChallenge.starterCode,
        testCases:   codingChallenge.testCases.filter((tc) => tc.expectedOutput.trim()),
      }
    }
    return payload
  }

  const handleEditSubsection = async () => {
    const v = getValues()
    const fd = new FormData()
    fd.append("sectionId",    modalData.sectionId)
    fd.append("subSectionId", modalData._id)
    fd.append("type",         subType)
    if (v.lectureTitle  !== modalData.title)         fd.append("title",        v.lectureTitle)
    if (description     !== modalData.description)   fd.append("description",  description)
    if (timeDuration    !== (modalData.timeDuration || "")) fd.append("timeDuration", timeDuration)
    if (videoMode === "url" && videoUrl !== modalData.videoUrl) fd.append("videoUrl", videoUrl)
    else if (videoMode === "upload" && v.lectureVideo instanceof File) fd.append("video", v.lectureVideo)
    if (subType === "quiz") fd.append("quizData", JSON.stringify(buildQuizPayload()))
    fd.append("pdfLinks", JSON.stringify(pdfLinks.filter((l) => l.name.trim() && l.url.trim())))
    setLoading(true)
    const result = await updateSubSection(fd, token)
    if (result) {
      const updated = course.courseContent.map((s) =>
        s._id === modalData.sectionId ? result : s
      )
      dispatch(setCourse({ ...course, courseContent: updated }))
    }
    setModalData(null)
    setLoading(false)
  }

  const onSubmit = async (data) => {
    if (view) return
    if (!description.replace(/<[^>]*>/g, "").trim()) { setDescError(true); return }
    setDescError(false)
    if (!validateVideo()) return

    if (edit) {
      if (!isFormUpdated()) toast.error("No changes made to the form")
      else handleEditSubsection()
      return
    }

    const fd = new FormData()
    fd.append("sectionId",   modalData)
    fd.append("title",       data.lectureTitle)
    fd.append("description", description)
    fd.append("type",        subType)
    if (timeDuration.trim()) fd.append("timeDuration", timeDuration.trim())
    if (subType === "video") {
      if (videoMode === "url" && videoUrl.trim()) fd.append("videoUrl", videoUrl)
      else if (videoMode === "upload" && data.lectureVideo instanceof File) fd.append("video", data.lectureVideo)
    }
    if (subType === "quiz") fd.append("quizData", JSON.stringify(buildQuizPayload()))
    fd.append("pdfLinks", JSON.stringify(pdfLinks.filter((l) => l.name.trim() && l.url.trim())))

    setLoading(true)
    const result = await createSubSection(fd, token)
    if (result) {
      const updated = course.courseContent.map((s) =>
        s._id === modalData ? result : s
      )
      dispatch(setCourse({ ...course, courseContent: updated }))
    }
    setModalData(null)
    setLoading(false)
  }

  const modeLabel = view ? "View" : add ? "Add" : "Edit"

  return (
    <div className="fixed inset-0 z-[1000] flex items-end justify-center bg-richblack-900/80 backdrop-blur-sm sm:items-center sm:p-4">
      <div
        className="flex w-full flex-col overflow-hidden rounded-t-2xl border border-richblack-600 bg-richblack-800 shadow-2xl sm:max-w-[720px] sm:rounded-2xl"
        style={{ maxHeight: "92dvh" }}
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-richblack-700 px-4 py-3 sm:px-6 sm:py-4">
          <div>
            <h2 className="text-sm font-semibold text-richblack-5 sm:text-base">{modeLabel} Lecture</h2>
            <p className="text-xs text-richblack-400">
              {view ? "Read-only view" : edit ? "Update lecture details" : "Add a new lecture"}
            </p>
          </div>
          <button
            type="button"
            disabled={loading}
            onClick={() => !loading && setModalData(null)}
            className="rounded-lg p-2 text-richblack-400 transition-colors hover:bg-richblack-700 hover:text-richblack-5 disabled:opacity-50"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col overflow-y-auto">
          <div className="space-y-5 px-4 py-4 sm:px-6 sm:py-5">

            {/* ── Type toggle ───────────────────────────────────── */}
            {!view && (
              <div className="flex rounded-xl border border-richblack-600 bg-richblack-700 p-1">
                {[
                  { val: "video", icon: FiUploadCloud, label: "Video Lecture" },
                  { val: "quiz",  icon: FiCode,        label: "In-Course Quiz" },
                ].map(({ val, icon: Icon, label }) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setSubType(val)}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium transition-all ${
                      subType === val
                        ? "bg-yellow-50 text-richblack-900"
                        : "text-richblack-400 hover:text-richblack-5"
                    }`}
                  >
                    <Icon size={15} /> {label}
                  </button>
                ))}
              </div>
            )}

            {/* ── Video section (only for video type) ──────────── */}
            {subType === "video" && (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="text-sm font-medium text-richblack-5">
                    Lecture Video {!view && <sup className="text-pink-200">*</sup>}
                  </label>
                  {!view && (
                    <div className="flex w-full items-center rounded-lg border border-richblack-600 bg-richblack-700 p-0.5 sm:w-auto">
                      <button type="button" onClick={() => handleModeSwitch("upload")}
                        className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-all sm:flex-none sm:py-1.5 ${
                          videoMode === "upload" ? "bg-yellow-50 text-richblack-900" : "text-richblack-400 hover:text-richblack-5"
                        }`}>
                        <FiUploadCloud size={13} /> Upload File
                      </button>
                      <button type="button" onClick={() => handleModeSwitch("url")}
                        className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-all sm:flex-none sm:py-1.5 ${
                          videoMode === "url" ? "bg-yellow-50 text-richblack-900" : "text-richblack-400 hover:text-richblack-5"
                        }`}>
                        <FiLink size={13} /> Paste URL
                      </button>
                    </div>
                  )}
                </div>

                {videoMode === "upload" && (
                  <>
                    <Upload
                      name="lectureVideo"
                      label=""
                      register={register}
                      setValue={(n, v) => { setValue(n, v); if (v) setVideoFileError(false) }}
                      errors={{}}
                      video
                      required={false}
                      viewData={view ? modalData.videoUrl : null}
                      editData={edit ? modalData.videoUrl : null}
                    />
                    {videoFileError && (
                      <p className="flex items-center gap-1 text-xs text-pink-200">
                        <FiAlertCircle size={12} /> Please select a video file
                      </p>
                    )}
                  </>
                )}

                {videoMode === "url" && (
                  <div className="flex flex-col gap-2">
                    <div className="relative">
                      <FiLink size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-richblack-400" />
                      <input
                        type="url"
                        disabled={view || loading}
                        value={videoUrl}
                        onChange={(e) => { setVideoUrl(e.target.value); if (videoUrlError) setVideoUrlError(false) }}
                        placeholder="https://example.com/video.mp4"
                        className="form-style w-full !pl-9"
                      />
                    </div>
                    {videoUrlError && (
                      <p className="flex items-center gap-1 text-xs text-pink-200">
                        <FiAlertCircle size={12} /> Please enter a valid video URL
                      </p>
                    )}
                    {videoUrl && looksLikeVideoUrl(videoUrl) && (
                      <div className="overflow-hidden rounded-xl border border-richblack-600 bg-black">
                        <video key={videoUrl} src={videoUrl} controls playsInline className="w-full"
                          style={{ display: "block", maxHeight: 240, background: "#000" }} />
                      </div>
                    )}

                    {/* Time Duration */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-richblack-5">
                        Time Duration{" "}
                        <span className="text-xs font-normal text-richblack-400">(e.g. 10:30 or 45 min)</span>
                      </label>
                      <div className="relative">
                        <FiClock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-richblack-400" />
                        <input
                          type="text"
                          disabled={view || loading}
                          value={timeDuration}
                          onChange={(e) => setTimeDuration(e.target.value)}
                          placeholder="e.g. 10:30"
                          className="form-style w-full !pl-9"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Title ────────────────────────────────────────── */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-richblack-5" htmlFor="lectureTitle">
                {subType === "quiz" ? "Quiz Title" : "Lecture Title"}{" "}
                {!view && <sup className="text-pink-200">*</sup>}
              </label>
              <input
                id="lectureTitle"
                disabled={view || loading}
                placeholder={subType === "quiz" ? "Enter a quiz title" : "Enter a clear, descriptive title"}
                {...register("lectureTitle", { required: true })}
                className="form-style w-full"
              />
              {errors.lectureTitle && (
                <p className="flex items-center gap-1 text-xs text-pink-200">
                  <FiAlertCircle size={12} /> Title is required
                </p>
              )}
            </div>

            {/* ── Description ──────────────────────────────────── */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-richblack-5">
                {subType === "quiz" ? "Quiz Instructions" : "Lecture Description"}{" "}
                {!view && <sup className="text-pink-200">*</sup>}
              </label>
              <RichTextEditor
                value={description}
                onChange={(html) => { setDescription(html); if (descError) setDescError(false) }}
                placeholder={subType === "quiz" ? "Describe what this quiz covers..." : "Describe what students will learn..."}
                disabled={view || loading}
                minHeight={100}
              />
              {descError && (
                <p className="flex items-center gap-1 text-xs text-pink-200">
                  <FiAlertCircle size={12} /> Description is required
                </p>
              )}
            </div>

            {/* ── PDF links (video type only) ───────────────────── */}
            {subType === "video" && (
              <PDFLinksBuilder
                links={pdfLinks}
                onChange={setPdfLinks}
                disabled={view || loading}
              />
            )}

            {/* ── Quiz builder ──────────────────────────────────── */}
            {subType === "quiz" && (
              <>
                {/* Passing score */}
                {!view && (
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-richblack-5">Passing Score (%)</label>
                    <input
                      type="number" min={0} max={100}
                      disabled={view || loading}
                      value={quizState.passingScore}
                      onChange={(e) => setQuizState((s) => ({ ...s, passingScore: Number(e.target.value) }))}
                      className="form-style w-24 text-center"
                    />
                  </div>
                )}

                {/* MCQ */}
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-semibold text-richblack-100">MCQ Questions</p>
                  <MCQBuilder
                    questions={quizState.mcqQuestions}
                    onChange={(qs) => setQuizState((s) => ({ ...s, mcqQuestions: qs }))}
                    disabled={view || loading}
                  />
                </div>

                {/* Coding challenge */}
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-semibold text-richblack-100">Coding Challenge</p>
                  <CodingChallengeBuilder
                    data={quizState.codingChallenge}
                    onChange={(cc) => setQuizState((s) => ({ ...s, codingChallenge: cc }))}
                    disabled={view || loading}
                  />
                </div>
              </>
            )}
          </div>

          {/* ── Sticky action bar ─────────────────────────────── */}
          {!view && (
            <div className="flex flex-shrink-0 items-center justify-end gap-3 border-t border-richblack-700 bg-richblack-800 px-4 py-3 sm:px-6 sm:py-4">
              <button type="button" disabled={loading} onClick={() => setModalData(null)}
                className="flex-1 rounded-xl border border-richblack-600 py-2.5 text-sm font-medium text-richblack-300 transition-colors hover:border-richblack-500 hover:text-richblack-100 disabled:opacity-50 sm:flex-none sm:px-5">
                Cancel
              </button>
              <button type="submit" disabled={loading}
                className="flex-1 rounded-xl bg-yellow-50 py-2.5 text-sm font-bold text-richblack-900 transition-all hover:bg-yellow-100 active:scale-95 disabled:opacity-60 sm:flex-none sm:px-6">
                {loading ? "Saving…" : edit ? "Save Changes" : subType === "quiz" ? "Add Quiz" : "Add Lecture"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}
